import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { IconLibrary } from '@product-framework/index';

// Stand-in card art when a document has no uploaded thumbnail: a tinted
// gradient, a faded category icon and the category as an overline.
const CATEGORY_ICONS = {
  'Getting Started': IconLibrary.Help,
  Claims: IconLibrary.Claims,
  Dispatch: IconLibrary.Dispatch,
  Insights: IconLibrary.Performance,
};
const CATEGORY_TINTS = {
  'Getting Started': 'primary',
  Claims: 'warning',
  Dispatch: 'success',
  Insights: 'info',
};
const TINT_ROTATION = ['primary', 'success', 'warning', 'info', 'error'];

const tintKey = category => {
  if (CATEGORY_TINTS[category]) return CATEGORY_TINTS[category];
  const name = String(category || '');
  let hash = 0;
  for (let index = 0; index < name.length; index += 1)
    hash = (hash * 31 + name.charCodeAt(index)) % 997;
  return TINT_ROTATION[hash % TINT_ROTATION.length];
};
const tone = (theme, category) =>
  theme.palette[tintKey(category)] || theme.palette.primary;

export default function CategoryBanner({
  category,
  thumbnailUrl,
  title,
  height = 140,
}) {
  const Icon = CATEGORY_ICONS[category] || IconLibrary.Note;
  const [failedUrl, setFailedUrl] = useState(null);
  const showImage = Boolean(thumbnailUrl) && failedUrl !== thumbnailUrl;
  return (
    <Box
      data-testid="category-banner"
      sx={theme => ({
        position: 'relative',
        overflow: 'hidden',
        height,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        px: 2,
        py: 1.5,
        backgroundImage: `linear-gradient(115deg, ${alpha(
          tone(theme, category).main,
          0.34,
        )} 0%, ${alpha(tone(theme, category).main, 0.16)} 46%, ${alpha(
          tone(theme, category).main,
          0.05,
        )} 100%)`,
      })}>
      {showImage && (
        <Box
          component="img"
          src={thumbnailUrl}
          alt=""
          onError={() => setFailedUrl(thumbnailUrl)}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
      {showImage && (
        <Box
          sx={theme => ({
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(transparent, ${alpha(
              theme.palette.background.paper,
              0.95,
            )})`,
          })}
        />
      )}
      {!showImage && (
        <Icon
          aria-hidden
          sx={theme => ({
            position: 'absolute',
            top: '50%',
            right: -18,
            transform: 'translateY(-50%) rotate(-10deg)',
            fontSize: 132,
            color: alpha(
              tone(theme, category).dark || tone(theme, category).main,
              0.16,
            ),
            pointerEvents: 'none',
          })}
        />
      )}
      <Typography
        variant="overline"
        sx={theme => ({
          position: 'relative',
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: tone(theme, category).dark || tone(theme, category).main,
        })}>
        {category || ''}
      </Typography>
      {title && (
        <Typography
          variant="subtitle1"
          sx={{
            position: 'relative',
            fontWeight: 500,
            color: 'text.primary',
            maxWidth: '78%',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
          {title}
        </Typography>
      )}
    </Box>
  );
}
